// components/Notifications/index.jsx
import { useState, useEffect } from "react";
import {
  Popover,
  IconButton,
  Typography,
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

const TEABLE_TOKEN = process.env.REACT_APP_TEABLE_API_TOKEN_FDO;

const NOTIFICATION_API = `/api/fdo/notifications`;

export default function Notifications({ open, onClose, anchorEl, onNotificationClick }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;

    const fetchNotifications = async () => {
      setLoading(true);
      setNotifications([]);

      try {
        const res = await fetch(NOTIFICATION_API, {
          headers: { "Content-Type": "application/json" }
        });

        if (!res.ok) throw new Error("Failed to fetch");

        const data = await res.json();

        if (data.success && data.records) {
          const notifs = data.records
            .map((r) => ({
              id: r.id,
              type: r.type || "unknown",
              text: r.text || r.message || "No message",
              author: r.user || r.user_name || "Unknown",
              timestamp: r.createdAt || new Date().toISOString(),
              avatar: (r.user || r.user_name || "U").charAt(0).toUpperCase(),
              apt: r.apt || "N/A",
              guestName: r.guest_name || "Unknown",
              reservationId: r.reservation_id,
            }))
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

          setNotifications(notifs);
        }

      } catch (err) {
        console.error("Failed to load notifications:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [open]);

  const getTypeLabel = (type) => {
    switch (type) {
      case "comment": return "New Comment";
      case "comment_edit": return "Comment Edited";
      case "reply": return "New Reply";
      case "reply_edit": return "Reply Edited";
      default: return type;
    }
  };

  return (
    <Popover
      open={open}
      onClose={onClose}
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      elevation={0}
      PaperProps={{
        sx: {
          width: 380,
          maxHeight: 500,
          borderRadius: 2,
          boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
          overflow: "hidden",
          backgroundColor: "#ffffff !important",
          backgroundImage: "none",
          border: "1px solid #e5e7eb",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: "1px solid #e5e7eb",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "#f9fafb",
        }}
      >
        <MDTypography variant="h6" fontWeight="bold" fontSize="1rem">
          Notifications ({notifications.length})
        </MDTypography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ maxHeight: 400, overflow: "auto" }}>
        {loading ? (
          <MDBox display="flex" justifyContent="center" p={3}>
            <CircularProgress size={24} />
          </MDBox>
        ) : notifications.length === 0 ? (
          <MDBox p={3} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              No notifications yet.
            </Typography>
          </MDBox>
        ) : (
          <List disablePadding>
            {notifications.map((notif, idx) => (
              <Box key={notif.id}>
                <ListItem
                  alignItems="flex-start"
                  sx={{
                    py: 1.5,
                    px: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#f3f4f6',
                    },
                    transition: 'background-color 0.2s ease',
                  }}
                  onClick={() => {
                    if (onNotificationClick && notif.reservationId) {
                      onNotificationClick(notif.reservationId);
                    }
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor: "#f3f4f6",
                        color: "#374151",
                        fontSize: "0.75rem",
                        fontWeight: "bold",
                      }}
                    >
                      {notif.author?.charAt(0)?.toUpperCase() || "U"}
                    </Avatar>
                  </ListItemAvatar>

                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          flexWrap: "wrap",
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          lineHeight: 1.4,
                        }}
                      >
                        <span>{notif.author}</span>

                        <Typography component="span" variant="caption">
                          {notif.type}
                        </Typography>

                        <Typography component="span" variant="caption">
                          on
                        </Typography>

                        <Typography component="span" variant="caption">{notif.guestName}</Typography>

                        <Typography component="span" variant="caption">{notif.apt}</Typography>
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.primary"
                          sx={{ display: "block", mb: 0.5, mt: 1, fontSize: "0.875rem", fontWeight: "bold", }}
                        >
                          {notif.text}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(notif.timestamp).toLocaleString([], {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
                {idx < notifications.length - 1 && <Divider />}
              </Box>
            ))}
          </List>
        )}
      </Box>
    </Popover>
  );
}